import React, { FC, memo, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import moment from 'moment';
import { Calendar } from '../Cal';
import TextField from '../../TextField/TextField';
import { CalendarDay } from '../calendar-day';
import { displayDay, isCurrentMonth } from '../utils';
import { Timestamp } from '../types';
import { useCalendarMonth } from '../use-calendar-month';
import { useDatadogLog } from '../../logging';
import Icon from '../../../DataDisplay/Icon/Icon';
import styles from './DatePickerSimple.module.scss';
import ErrorText from '../../../Text/ErrorText/ErrorText';
import OutsideClickHandler from '../../../../vendor/OutsideClickHandler';
import PopOver from '../../../Utility/PopOver/PopOver';

export type DatePickerSimpleProps = {
  title?: string;
  variant?: 'underlined' | 'lined' | 'outline' | 'outlineShadow';
  isInline?: boolean;
  iconPosition?: 'left';
  format?: 'number' | 'date' | 'timestamp' | 'date-comma';
  position?: 'topLeft' | 'topRight' | 'topCenter' | 'bottomLeft' | 'bottomRight';
  selected?: Timestamp;
  placeholder?: string;
  placeholderColor?: string;
  borderColor?: string;
  style?: {
    '--datePickerSimpleTriggerBorderRadius'?: string;
  };
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onOpen?: (isOpen: boolean) => void;
  name: string;
  value: string;
  min?: Timestamp;
  max?: Timestamp;
  error?: string | null;
  hasInvalidDate?: boolean;
  isDisabled?: boolean;
  startYearOffset?: number;
  className?: string;
  allowBlank?: boolean;
  inputWidth?: string;
};

const DatePickerSimple: FC<DatePickerSimpleProps> = ({
  title,
  variant = 'underlined',
  iconPosition,
  format = 'date',
  position,
  value,
  error = null,
  name,
  placeholder = 'YYYY-MM-DD',
  placeholderColor,
  borderColor,
  selected = 0 as Timestamp,
  min,
  max,
  hasInvalidDate = false,
  isDisabled = false,
  isInline = false,
  style,
  startYearOffset,
  className,
  allowBlank = false,
  inputWidth = '110px',
  onChange = () => {},
  onOpen = () => {},
  onBlur = () => {},
}) => {
  const [dateValue, setDateValue] = useState<string>(allowBlank ? value : '');
  const [dropDownValue, setDropDownValue] = useState<Timestamp>(selected);
  const [highlightedDate, setHighlightedDate] = useState<Timestamp>(selected);

  const { currentMonthTimestamp } = useCalendarMonth(dropDownValue || (Date.now() as Timestamp));

  const [isShownPicker, setShownPicker] = useState(false);
  const [isShouldShowPicker, setShouldShowPicker] = useState(false);
  const hiddenFileInput = React.useRef<HTMLInputElement>(null);
  const [selectedDate, setSelectedDate] = useState<string | Timestamp>();
  const inputEl = useRef<HTMLInputElement>(null);
  const pickerEl = useRef<HTMLInputElement>(null);
  const [positionInfo, setPositionInfo] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const [textDateInput, setTextDateInput] = useState(value);

  const displayDateNumber = (dayTimestamp: Timestamp | number): string => {
    const formattedDate = moment(dayTimestamp).format('YYYY-MM-DD');
    return formattedDate;
  };

  const log = useDatadogLog();

  useEffect(() => {
    if (value !== '' && !Number.isNaN(value)) {
      setDropDownValue(moment(Number(value)).valueOf() as Timestamp);
      setHighlightedDate(moment(Number(value)).startOf('day').valueOf() as Timestamp);
      setTextDateInput(displayDateNumber(Number(value)));
    } else {
      setHighlightedDate(0 as Timestamp);
      setTextDateInput('');
    }
  }, [value]);

  const displayDate = (dayTimestamp: Timestamp | number | string): string => {
    const date = new Date(dayTimestamp);

    return format === 'date'
      ? date.toDateString().substring(4)
      : date
          .toDateString()
          .substring(4)
          .replace(/(\d{1,2}) (\d{4})/, '$1, $2');
  };
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement | null;

      let isWithinPopover = false;
      let currentElement = target;

      while (currentElement) {
        if (popoverRef.current && currentElement === popoverRef.current) {
          isWithinPopover = true;
          break;
        }
        currentElement = currentElement.parentElement;
      }

      if (!isWithinPopover) {
        setShouldShowPicker(false);
      }
    };
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const calStyle = {
    '--picker-height': `${pickerEl.current?.clientHeight || '0'}px`,
  } as React.CSSProperties;

  const handleSelect = useCallback(
    (nextValue: Timestamp) => {
      const valueInString = String(nextValue);

      setShouldShowPicker(false);
      setDateValue(valueInString);
      setTextDateInput(displayDateNumber(nextValue));
      log({
        message: `${displayDateNumber(nextValue)} was selected in ${name}`,
        payload: { name, value: displayDateNumber(nextValue) },
        actionType: 'Input',
      });
      if (hiddenFileInput.current !== null) {
        hiddenFileInput.current.click();
      }
    },
    [onChange],
  );

  useEffect(() => {
    if (onOpen) onOpen(isShownPicker);
  }, [isShownPicker]);

  const valueString = useMemo(() => {
    if (!selectedDate) {
      return <div style={{ color: placeholderColor }}>{placeholder}</div>;
    }

    return format === 'date' || format === 'date-comma'
      ? `${displayDate(Number(selectedDate))}`
      : format === 'timestamp'
        ? `${displayDateNumber(Number(selectedDate))}`
        : selectedDate.toString().includes('-')
          ? selectedDate
          : `${displayDateNumber(Number(selectedDate))}`;
  }, [selectedDate]);

  const forceFireOnChange = useCallback(() => {
    /*

    Note: this is an established hack for causing React's onChange synthetic event to fire.
    https://github.com/facebook/react/issues/11488

    Typically, input's onChange function only fires from direct user input. In this case we are using react state
    to set the value of the input, so we need to fire onChange manually.

    We are doing this so that this component can generate a React.ChangeEvent for any onChange callback that is given to it.

    */

    interface ExtendedElement extends HTMLInputElement {
      _valueTracker: { setValue: (val: string | undefined) => void };
    }

    const currentElement = inputEl.current as ExtendedElement;
    if (currentElement) {
      const lastValue = currentElement.value;

      currentElement.value = dateValue;
      currentElement._valueTracker?.setValue(lastValue);
    }

    /* eslint-enable-line @typescript-eslint/no-unsafe-call */

    inputEl.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, [dateValue]);

  useEffect(() => {
    forceFireOnChange();
    setSelectedDate(value);
  }, [dateValue, value]);

  const onToggleShow = useCallback(() => setShouldShowPicker((state) => !state), []);

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length === 10) {
      handleSelect(moment(newValue).valueOf() as Timestamp);
      setDropDownValue(moment(newValue).valueOf() as Timestamp);
    } else if (newValue === '' && allowBlank) {
      setDateValue(newValue);
    }
    setTextDateInput(e.target.value);
  };
  const handleOnBlur = () => {
    if (value !== '') {
      setTextDateInput(displayDateNumber(Number(value)));
    }
  };

  useEffect(() => {
    if (pickerEl.current) {
      const iconRect = pickerEl.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const calendarHeight = 360; // Approximate height of calendar dropdown
      const spaceBelow = viewportHeight - iconRect.bottom;
      const spaceAbove = iconRect.top;

      // Check if there's not enough space below but more space above
      const shouldFlip = !position && spaceBelow < calendarHeight && spaceAbove > spaceBelow;

      setPositionInfo({
        top: shouldFlip ? iconRect.top - calendarHeight : iconRect.bottom,
        left: iconRect.left,
        width: iconRect.width,
        height: 0,
      });
    }
    setShownPicker(isShouldShowPicker);
  }, [isShouldShowPicker]);

  const renderCalendar = (
    <OutsideClickHandler
      onOutsideClick={() => {
        setShouldShowPicker(false);
        setDropDownValue(selected);
      }}
    >
      <Calendar
        title={title}
        monthTimestamp={currentMonthTimestamp}
        isInline={isInline}
        style={calStyle}
        setDropDownValue={setDropDownValue}
        position={position}
        startYearOffset={startYearOffset}
      >
        {(dayTimestamp) => (
          <CalendarDay
            key={String(dayTimestamp)}
            isCurrentMonth={isCurrentMonth(currentMonthTimestamp, dayTimestamp)}
            isSelected={highlightedDate === dayTimestamp}
            dayTimestamp={dayTimestamp}
            onClick={handleSelect}
            onDisable={() =>
              (min && dayTimestamp < new Date(min).setHours(0, 0, 0, 0)) ||
              (max && dayTimestamp > new Date(max).setHours(0, 0, 0, 0)) ||
              false
            }
          >
            {displayDay(dayTimestamp)}
          </CalendarDay>
        )}
      </Calendar>
    </OutsideClickHandler>
  );

  return (
    <div
      ref={pickerEl}
      className={[styles['input-date']].join(' ')}
      style={style}
      data-testid={`datePickerSimple-${name}`}
    >
      {!isInline && (
        <>
          {format === 'timestamp' ? (
            <div
              className={[
                styles['input-date-control-range'],
                getErrorClass(),
                styles[variant],
                getActiveClass(),
                getInvalidDateErrorClass(),
                className,
              ].join(' ')}
              role="presentation"
            >
              <Icon
                height="20px"
                width="20px"
                icon="calendar"
                position="center"
                color={variant === 'lined' ? 'dark-grey' : 'medium-dark'}
                onClick={onToggleShow}
              />
              <div aria-hidden onClick={onToggleShow}>
                <TextField
                  variant="calendar-header"
                  name="date"
                  preset="date"
                  onBlur={() => {
                    handleOnBlur();
                  }}
                  onChange={(e) => {
                    handleDateInput(e);
                  }}
                  placeholder={placeholder}
                  value={textDateInput}
                  height={variant === 'outlineShadow' ? '24px' : '20px'}
                  width={inputWidth}
                  padding="2px"
                />
              </div>
            </div>
          ) : (
            <div
              className={[
                styles['input-date-control-text'],
                getErrorClass(),
                styles[variant],
                getActiveClass(),
                styles[getIconPositionClass()],
                getDisabledClass(),
                className,
              ].join(' ')}
              style={{ borderColor }}
              onClick={onToggleShow}
              role="presentation"
            >
              <div className={styles.iconContainer}>
                <Icon
                  height="15px"
                  width="15px"
                  icon="calendar"
                  position="center"
                  color={variant === 'lined' ? 'dark-grey' : 'medium-dark'}
                />
              </div>
              <div className={styles['input-date-control-label']}>{valueString}</div>
            </div>
          )}
        </>
      )}
      {(isShownPicker || isInline) && !isDisabled && (
        <>
          {!isInline && (
            <PopOver ref={popoverRef} style={positionInfo}>
              {renderCalendar}
            </PopOver>
          )}
          {isInline && renderCalendar}
        </>
      )}
      <input
        id={`${name}`}
        name={`${name}`}
        type="text"
        className={styles.hidden}
        ref={inputEl}
        onBlur={onBlur}
        style={{ display: 'none' }}
        onChange={(e) => {
          onChange(e);
        }}
      />
      <ErrorText error={error} size="sm" indentation />
    </div>
  );

  function getErrorClass() {
    return error ? styles.hasError : '';
  }

  function getActiveClass() {
    return isShownPicker && !isDisabled ? styles.active : '';
  }

  function getInvalidDateErrorClass() {
    return hasInvalidDate ? styles.invalidDate : '';
  }

  function getDisabledClass() {
    return isDisabled ? styles.disabled : '';
  }

  function getIconPositionClass() {
    return iconPosition === 'left' ? 'iconPositionLeft' : '';
  }
};

const DatepickerSimpleMemo = memo(DatePickerSimple);
export { DatepickerSimpleMemo as DatepickerSimple };
